import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiCode, FiGitBranch, FiTrendingUp, FiZap, FiShield, FiBarChart, FiCheckCircle, FiArrowRight, FiMonitor } from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import { useSEO } from '../hooks/useSEO';
import './APM.css';

const DevOpsObservability = () => {
  useSEO({
    title: 'DevOps Observability - Monitoring Tools for DevOps Teams | LogCrest',
    description: 'Complete DevOps observability platform with log management, infrastructure monitoring, and AI-powered insights. Best DevOps monitoring tools for modern teams.',
    keywords: 'DevOps observability, DevOps monitoring tools, DevOps platform, DevOps monitoring, SRE tools, site reliability engineering, DevOps analytics, CI/CD monitoring'
  });

  useEffect(() => {
    trackPageView('/devops-observability', {
      page_title: 'LogCrest DevOps Observability',
      page_category: 'devops-observability'
    });
  }, []);

  const features = [
    {
      icon: <FiCode />,
      title: 'CI/CD Monitoring',
      description: 'Monitor your CI/CD pipelines with real-time visibility into builds, deployments, and releases.'
    },
    {
      icon: <FiGitBranch />,
      title: 'DevOps Analytics',
      description: 'Comprehensive analytics for DevOps metrics, performance, and team productivity.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'SRE Tools',
      description: 'Site reliability engineering tools for monitoring, alerting, and incident response.'
    },
    {
      icon: <FiZap />,
      title: 'Infrastructure as Code Monitoring',
      description: 'Monitor infrastructure changes and deployments with full observability.'
    },
    {
      icon: <FiBarChart />,
      title: 'DevOps Dashboards',
      description: 'Unified dashboards for DevOps teams with real-time metrics and insights.'
    },
    {
      icon: <FiShield />,
      title: 'Security & Compliance',
      description: 'DevOps security monitoring and compliance tracking for modern teams.'
    }
  ];

  const benefits = [
    'Complete DevOps observability platform',
    'CI/CD monitoring and analytics',
    'SRE tools for site reliability engineering',
    'DevOps monitoring tools for modern teams',
    'Infrastructure as Code monitoring',
    'Best DevOps platform for startups and enterprises'
  ];

  return (
    <div className="agent-sre-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>DevOps Observability Platform</h1>
            <p className="hero-subtitle">
              Best DevOps monitoring tools for modern teams. Complete DevOps observability with log management, infrastructure monitoring, and AI-powered insights.
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                  <FiArrowRight />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>DevOps Monitoring Tools</h2>
            <p>Complete DevOps observability for modern development teams</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose DevOps Observability?</h2>
              <p>
                Our DevOps observability platform provides complete visibility into your development 
                and operations with SRE tools and DevOps monitoring capabilities.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>DevOps Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiCode />
                    <span>Deployments: 245</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>CI/CD Success: 98%</span>
                  </div>
                  <div className="metric">
                    <FiBarChart />
                    <span>MTTR: 15min</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready for DevOps Observability?</h2>
            <p>Experience the best DevOps monitoring tools for your development and operations teams.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
                <FiArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevOpsObservability;

